import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, PlusCircle, LogOut, Moon, X, Loader2, RefreshCw, Sparkles, ClipboardList, Settings, Copy, Database } from 'lucide-react';
import Logo from '../../components/common/Logo';
import { mockReports } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import apiService from '../../services/api';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ total: 0, pendingRoutine: 0, pendingAnalysis: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar dados do backend
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Carregar relatórios e estatísticas do backend
      const [reportsData, statsData] = await Promise.all([
        apiService.getReports(),
        apiService.getStats()
      ]);
      
      // Converter dados do backend para formato do frontend
      const convertedReports = reportsData.map(r => ({
        id: r.id,
        babyName: r.baby_name || 'Não informado',
        motherName: r.mother_name || 'Não informado',
        babyAge: r.baby_age || '-',
        status: r.status || 'pending_form',
        createdAt: r.created_at ? r.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        completedDays: r.completed_days || 0
      }));
      
      setReports(convertedReports);
      setStats({
        total: statsData.total,
        pendingRoutine: statsData.pending_routine,
        pendingAnalysis: statsData.pending_analysis,
        approved: statsData.approved
      });
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados. Usando dados locais.');
      // Fallback para mock data
      setReports(mockReports);
      setStats({
        total: mockReports.length,
        pendingRoutine: mockReports.filter(r => r.status === 'pending_routine').length,
        pendingAnalysis: mockReports.filter(r => r.status === 'pending_analysis').length,
        approved: mockReports.filter(r => r.status === 'approved').length
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrar relatórios baseado no filtro ativo
  const filteredReports = activeFilter === 'all' 
    ? reports 
    : reports.filter(r => r.status === activeFilter);

  const statusLabels = {
    pending_form: { label: 'Aguardando formulário', color: 'bg-gray-100 text-gray-600' },
    pending_routine: { label: 'Registrando rotina', color: 'bg-yellow-100 text-yellow-700' },
    pending_analysis: { label: 'Aguardando análise', color: 'bg-blue-100 text-blue-700' },
    pending_approval: { label: 'Aguardando aprovação', color: 'bg-purple-100 text-purple-700' },
    approved: { label: 'Aprovado', color: 'bg-green-100 text-green-700' }
  };

  // Configuração dos filtros para os cards
  const filterConfig = [
    { key: 'all', label: 'Total', value: stats.total, color: 'from-[#8F7AC5] to-[#30244F]' },
    { key: 'pending_routine', label: 'Registrando', value: stats.pendingRoutine, color: 'from-[#F2ACAA] to-[#D98DAE]' },
    { key: 'pending_analysis', label: 'Para analisar', value: stats.pendingAnalysis, color: 'from-[#D98DAE] to-[#B47CBF]' },
    { key: 'approved', label: 'Aprovados', value: stats.approved, color: 'from-[#7BC47F] to-[#5BA35F]' }
  ];

  const handleFilterClick = (filterKey) => {
    setActiveFilter(prev => prev === filterKey ? 'all' : filterKey);
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' },
            { icon: Database, label: 'Exportar Dados', path: '/admin/export' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header with Menu */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin/new')}
                className="bg-[#8F7AC5] text-white hover:bg-[#7A68A8]"
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Novo
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-[#9890A8]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Mobile Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Link
              to="/admin/dashboard"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/dashboard'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Link>
            <Link
              to="/admin/new"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/new'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <PlusCircle className="w-4 h-4" />
              Novo
            </Link>
            <Link
              to="/admin/manual"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/manual'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              Manual
            </Link>
            <Link
              to="/admin/reports"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/reports'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <FileText className="w-4 h-4" />
              Relatórios
            </Link>
            <Link
              to="/admin/templates"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/templates'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <Copy className="w-4 h-4" />
              Templates
            </Link>
            <Link
              to="/admin/training"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/training'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Treinar IA
            </Link>
            <Link
              to="/admin/form-builder"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/form-builder'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <Settings className="w-4 h-4" />
              Formulário
            </Link>
            <Link
              to="/admin/export"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/export'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <Database className="w-4 h-4" />
              Exportar
            </Link>
          </div>
        </div>

        {/* Welcome */}
        <motion.div
          className="mb-8 flex items-center justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#30244F]">Olá, Mari! 💜</h1>
            <p className="text-[#6B5B8C]">Aqui está o resumo das suas mentorias</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="text-[#8F7AC5]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </motion.div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
          </div>
        ) : (
          <>
        {/* Stats Cards - Filtros Clicáveis */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {filterConfig.map((filter, i) => (
            <motion.button
              key={filter.key}
              onClick={() => handleFilterClick(filter.key)}
              data-testid={`filter-${filter.key}`}
              className={`bg-gradient-to-br ${filter.color} rounded-2xl p-6 text-white text-left transition-all duration-200 ${
                activeFilter === filter.key 
                  ? 'ring-4 ring-white/50 scale-[1.02] shadow-lg' 
                  : 'hover:scale-[1.02] hover:shadow-md'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <p className="text-white/80 text-sm">{filter.label}</p>
                {activeFilter === filter.key && filter.key !== 'all' && (
                  <X className="w-4 h-4 text-white/70" />
                )}
              </div>
              <p className="text-3xl font-bold">{filter.value}</p>
              {activeFilter === filter.key && filter.key !== 'all' && (
                <p className="text-xs text-white/60 mt-1">Clique para remover filtro</p>
              )}
            </motion.button>
          ))}
        </div>

        {/* Indicador de filtro ativo */}
        {activeFilter !== 'all' && (
          <motion.div 
            className="mb-4 flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-sm text-[#6B5B8C]">
              Filtrando por: <strong className="text-[#8F7AC5]">{filterConfig.find(f => f.key === activeFilter)?.label}</strong>
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setActiveFilter('all')}
              className="text-[#9890A8] hover:text-[#30244F] p-1 h-auto"
            >
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {/* Recent Reports */}
        <div className="bg-white rounded-3xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#30244F]">
              {activeFilter === 'all' ? 'Relatórios Recentes' : `Relatórios - ${filterConfig.find(f => f.key === activeFilter)?.label}`}
            </h2>
            <Button
              variant="ghost"
              className="text-[#8F7AC5]"
              onClick={() => navigate('/admin/reports')}
            >
              Ver todos
            </Button>
          </div>

          <div className="space-y-4">
            {filteredReports.length === 0 ? (
              <div className="text-center py-8 text-[#9890A8]">
                <p>Nenhum relatório encontrado com este filtro.</p>
                <Button 
                  variant="ghost" 
                  className="mt-2 text-[#8F7AC5]"
                  onClick={() => setActiveFilter('all')}
                >
                  Ver todos os relatórios
                </Button>
              </div>
            ) : (
              filteredReports.slice(0, 5).map((report, i) => (
                <motion.div
                  key={report.id}
                  className="flex items-center justify-between p-4 bg-[#FBF4FF] rounded-2xl hover:bg-[#FAE0D5]/30 transition-colors cursor-pointer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => navigate(`/admin/reports/${report.id}`)}
                  data-testid={`report-item-${report.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#D98DAE] to-[#8F7AC5] rounded-full flex items-center justify-center text-white font-semibold">
                      {report.babyName?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-[#30244F]">{report.babyName}</p>
                      <p className="text-sm text-[#9890A8]">{report.motherName} • {report.babyAge} meses</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusLabels[report.status]?.color}`}>
                      {statusLabels[report.status]?.label}
                    </span>
                    <p className="text-xs text-[#9890A8] mt-1">{report.completedDays}/5 dias</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
